import type { EditAreaTemplateApplication } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import {
  EditAreaTemplateSummary,
  useProductEditAreaTemplateQuery,
} from '@/entities/edit-area-template';
import { Button } from '@/shared/ui';
import { useApplyEditAreaTemplate } from '../model/use-apply-edit-area-template';
import { useCopyEditAreaFromProduct } from '../model/use-copy-edit-area-from-product';
import { CopyEditAreaModal } from './copy-edit-area-modal';
import { TemplateFromProductModal } from './template-from-product-modal';

interface EditAreaTemplateActionsProps {
  readonly productId: string;
  readonly productName: string;
  /** 저장된 편집영역 버전이 있는가 — 있으면 덮어쓰기 확인을 받고 보조 동작으로 보여 준다. */
  readonly hasEditArea: boolean;
  /** 저장하지 않은 편집 내용이 있는가 — 있으면 버린다는 확인을 받는다. */
  readonly hasUnsavedChanges: boolean;
  readonly disabled: boolean;
  readonly onApplied: (application: EditAreaTemplateApplication, sourceLabel: string) => void;
}

const confirmReplace = (hasEditArea: boolean, hasUnsavedChanges: boolean): boolean => {
  if (!hasEditArea && !hasUnsavedChanges) return true;
  const lines = [
    hasUnsavedChanges ? '저장하지 않은 편집 내용은 사라집니다.' : null,
    hasEditArea
      ? '저장하면 새 버전으로 덮어씁니다 — 이전 버전은 [되돌리기]로 복구할 수 있습니다.'
      : null,
    '계속하시겠습니까?',
  ].filter((line) => line !== null);
  return window.confirm(lines.join('\n'));
};

/**
 * 상품 편집영역 화면의 템플릿 동작 — 카테고리 템플릿 적용과 다른 상품에서 복사.
 * 편집영역이 없으면 첫 단계로 크게, 있으면 보조 버튼으로 보여 준다. 결과 기하는 onApplied 로 넘겨 초안에 넣는다.
 */
export const EditAreaTemplateActions = ({
  productId,
  productName,
  hasEditArea,
  hasUnsavedChanges,
  disabled,
  onApplied,
}: EditAreaTemplateActionsProps): ReactElement => {
  const resolved = useProductEditAreaTemplateQuery(productId);
  const [isCopyOpen, setIsCopyOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createdNotice, setCreatedNotice] = useState(false);
  const [copySource, setCopySource] = useState('');
  const template = resolved.data?.template ?? null;
  const apply = useApplyEditAreaTemplate({
    onSuccess: (application) => onApplied(application, `템플릿 "${template?.name ?? ''}"`),
  });
  const copy = useCopyEditAreaFromProduct({
    onSuccess: (application) => {
      setIsCopyOpen(false);
      onApplied(application, `"${copySource}"`);
    },
  });
  const busy = disabled || apply.isPending || copy.isPending;

  const handleApply = (): void => {
    if (template === null || !confirmReplace(hasEditArea, hasUnsavedChanges)) return;
    apply.applyTemplate({ productId, templateId: template.id });
  };

  return (
    <section
      aria-label="편집영역 템플릿"
      className={
        hasEditArea ? 'rounded-lg border p-3' : 'rounded-lg border-2 border-primary/40 p-4'
      }
    >
      {hasEditArea ? null : (
        <p className="mb-3 text-sm">
          {template !== null
            ? '아직 편집영역이 없습니다. 카테고리 템플릿을 적용하면 면·표면·인쇄 영역이 채워지고, 색상별 목업 사진만 올린 뒤 저장하면 됩니다.'
            : '아직 편집영역이 없습니다. 같은 블랭크의 다른 상품에서 복사하거나 아래에서 직접 설정하세요.'}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {template !== null ? (
          <Button
            type="button"
            size={hasEditArea ? 'sm' : 'md'}
            variant={hasEditArea ? 'outline' : 'default'}
            disabled={busy}
            onClick={handleApply}
          >
            카테고리 템플릿 적용 ({template.name})
          </Button>
        ) : resolved.isPending ? null : (
          <span className="text-sm text-muted-foreground">
            이 상품 카테고리에 지정된 템플릿이 없습니다 — 기준 정보 관리 › 편집영역 템플릿에서
            지정할 수 있습니다.
          </span>
        )}
        <Button
          type="button"
          size={hasEditArea ? 'sm' : 'md'}
          variant="outline"
          disabled={busy}
          onClick={() => {
            if (confirmReplace(hasEditArea, hasUnsavedChanges)) setIsCopyOpen(true);
          }}
        >
          다른 상품에서 복사
        </Button>
        {hasEditArea ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setCreatedNotice(false);
              setIsCreateOpen(true);
            }}
          >
            이 상품 설정으로 템플릿 만들기
          </Button>
        ) : null}
      </div>
      {createdNotice ? (
        <p role="status" className="mt-2 text-sm">
          템플릿을 만들었습니다. 기준 정보 관리 › 편집영역 템플릿에서 확인할 수 있습니다.
        </p>
      ) : null}
      {template !== null && resolved.data ? (
        <div className="mt-2">
          <p className="text-xs text-muted-foreground">
            {resolved.data.inheritedFrom} 카테고리 템플릿 — 서피스는 사진 % 좌표라 원본과 같은 표준
            구도로 찍은 목업에서 그대로 맞습니다(
            <a href="#edit-area-photos" className="underline">
              목업 규격
            </a>
            ).
          </p>
          {hasEditArea ? null : <EditAreaTemplateSummary template={template} />}
        </div>
      ) : null}
      {apply.error ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {apply.error.message}
        </p>
      ) : null}
      {isCreateOpen ? (
        <TemplateFromProductModal
          initialProduct={{ id: productId, name: productName }}
          onCreated={() => {
            setIsCreateOpen(false);
            setCreatedNotice(true);
            void resolved.refetch();
          }}
          onClose={() => setIsCreateOpen(false)}
        />
      ) : null}
      {isCopyOpen ? (
        <CopyEditAreaModal
          productId={productId}
          isPending={copy.isPending}
          errorMessage={copy.error?.message ?? null}
          onPick={(source) => {
            setCopySource(source.name);
            copy.copyFromProduct({ productId, sourceProductId: source.id });
          }}
          onClose={() => setIsCopyOpen(false)}
        />
      ) : null}
    </section>
  );
};
